/**
 * 解密 + 校验 + 合并 / 完整替换恢复。
 *
 * 关键顺序：
 * 1. 解密、解 ZIP、全量校验（此时不写任何数据）；
 * 2. 账号绑定校验；
 * 3. 完整替换先导出并下载当前数据的安全备份；
 * 4. 记住当前 IndexedDB / localStorage / Mimo 快照；
 * 5. 写入；任何一步失败都回滚已修改的 IndexedDB 与 localStorage；
 * 6. 最后恢复服务端 TTS 音色偏好（失败只给警告，不回滚本地）。
 */
import type { BackupLedgerEntry } from '../../core/db';
import { getDb } from '../../core/db';
import type { ChatMessage, Moment, Preset } from '../../core/types';
import type { GalleryImage } from '../gallery/galleryRepo';
import type { LocalAudio, LocalReference, MimoSnapshot } from '../mimo/storage';
import { mimoStorage } from '../mimo/storage';
import { unpackArchive } from './archive';
import type { BackupArchive, BackupCollections } from './archive';
import { decryptContainer, isEncryptedBackup } from './container';
import { sha256Hex } from './codec';
import {
  applyBackupLocalStorage,
  restoreAllLocalStorage,
  snapshotAllLocalStorage,
} from './local-storage';
import { bytesToDataUrl } from './media';
import type { MediaFile } from './media';
import { collectEncryptedBackup, defaultTtsApi } from './collect';
import { BackupError } from './types';
import type {
  BackupAccountHistory,
  BackupMimoFile,
  BackupSummary,
  BackupTtsApi,
  BackupInspection,
  CreateBackupOptions,
  EncryptedBackup,
  RestoreBackupOptions,
  RestoreCounts,
  RestoreResult,
} from './types';

type MediaLookup = ReadonlyMap<string, MediaFile>;

interface DbSnapshot {
  conversations: BackupCollections['conversations'];
  messages: BackupCollections['messages'];
  presets: BackupCollections['presets'];
  providers: BackupCollections['providers'];
  memories: BackupCollections['memories'];
  gallery: BackupCollections['gallery'];
  moments: BackupCollections['moments'];
  automationEvents: BackupCollections['automationEvents'];
  backupLedgerEntries: BackupLedgerEntry[];
}

function summaryFromManifest(archive: BackupArchive): BackupSummary {
  const manifest = archive.manifest;
  return {
    backupId: manifest.backupId,
    exportedAt: manifest.exportedAt,
    accountId: manifest.account.id,
    accountEmail: manifest.account.email,
    counts: manifest.counts,
    mediaBytes: manifest.media.bytes,
    ledger: manifest.ledger,
    warnings: [...manifest.warnings],
  };
}

/** 校验密码并读取摘要；不写入任何数据。 */
export async function inspectEncryptedBackup(
  data: Uint8Array,
  password: string,
): Promise<BackupInspection> {
  if (!isEncryptedBackup(data)) {
    throw new BackupError('FORMAT', '不是有效的 .zxbak 加密备份文件');
  }
  const zip = await decryptContainer(data, password);
  const archive = await unpackArchive(zip);
  return { summary: summaryFromManifest(archive), manifest: archive.manifest };
}

function assetHash(ref: string): string {
  if (!ref.startsWith('asset:')) throw new BackupError('VALIDATION', `非法资源引用：${ref}`);
  const hash = ref.slice('asset:'.length);
  if (!/^[a-f0-9]{64}$/.test(hash)) throw new BackupError('VALIDATION', `非法资源引用：${ref}`);
  return hash;
}

function mediaFor(media: MediaLookup, ref: string): MediaFile {
  const found = media.get(assetHash(ref));
  if (found === undefined) throw new BackupError('VALIDATION', `资源缺失：${ref}`);
  return found;
}

function restoreAssetString(value: string, media: MediaLookup): string {
  if (!value.startsWith('asset:')) return value;
  const found = mediaFor(media, value);
  return bytesToDataUrl(found.bytes, found.mime);
}

function restoreMessages(messages: ChatMessage[], media: MediaLookup): ChatMessage[] {
  return messages.map((message) => {
    const next: ChatMessage = { ...message };
    if (message.attachments !== undefined) {
      next.attachments = message.attachments.map((attachment) => {
        if (!attachment.dataUrl?.startsWith('asset:')) {
          return { ...attachment };
        }
        return { ...attachment, dataUrl: restoreAssetString(attachment.dataUrl, media) };
      });
    }
    if (message.images !== undefined) {
      next.images = message.images.map((image) => restoreAssetString(image, media));
    }
    if (message.audioUrl !== undefined) {
      next.audioUrl = restoreAssetString(message.audioUrl, media);
    }
    return next;
  });
}

function restorePresets(presets: Preset[], media: MediaLookup): Preset[] {
  return presets.map((preset) => {
    if (!preset.avatar?.startsWith('asset:')) return { ...preset };
    return { ...preset, avatar: restoreAssetString(preset.avatar, media) };
  });
}

function restoreGallery(gallery: GalleryImage[], media: MediaLookup): GalleryImage[] {
  return gallery.map((image) => {
    if (!image.dataUrl.startsWith('asset:')) return { ...image };
    return { ...image, dataUrl: restoreAssetString(image.dataUrl, media) };
  });
}

function restoreMoments(moments: Moment[], media: MediaLookup): Moment[] {
  return moments.map((moment) => ({
    ...moment,
    ...(moment.authorAvatar === undefined
      ? {}
      : { authorAvatar: restoreAssetString(moment.authorAvatar, media) }),
    images: moment.images.map((image) => restoreAssetString(image, media)),
  }));
}

async function restoreMimoSnapshot(
  file: BackupMimoFile,
  userId: string,
  media: MediaLookup,
): Promise<MimoSnapshot> {
  const audio: LocalAudio[] = [];
  for (const entry of file.audio) {
    const found = mediaFor(media, entry.mediaRef);
    const actualSha = await sha256Hex(found.bytes);
    if (actualSha !== entry.sha256 || found.bytes.length !== entry.bytes) {
      throw new BackupError('VALIDATION', `TTS 音频校验失败：${entry.key}`);
    }
    audio.push({
      userId,
      key: entry.key,
      id: entry.id,
      sha256: actualSha,
      bytes: found.bytes.length,
      mime: 'audio/wav',
      blob: new Blob([found.bytes], { type: 'audio/wav' }),
      links: [...entry.links],
      preview: entry.preview,
      createdAt: entry.createdAt,
    });
  }
  let reference: LocalReference | undefined;
  if (file.reference !== undefined) {
    const found = mediaFor(media, file.reference.mediaRef);
    const actualSha = await sha256Hex(found.bytes);
    if (actualSha !== file.reference.sha256) {
      throw new BackupError('VALIDATION', '参考音频校验失败');
    }
    reference = {
      userId,
      blob: new Blob([found.bytes], { type: file.reference.mime }),
      name: file.reference.name,
      sha256: actualSha,
      consent: true,
    };
  }
  return {
    audio,
    modes: file.modes.map((entry) => ({ ...entry })),
    ...(reference === undefined ? {} : { reference }),
  };
}

async function snapshotDatabase(): Promise<DbSnapshot> {
  const db = getDb();
  const [conversations, messages, presets, providers, memories, gallery, moments, automationEvents, backupLedgerEntries] =
    await Promise.all([
      db.conversations.toArray(),
      db.messages.toArray(),
      db.presets.toArray(),
      db.providers.toArray(),
      db.memories.toArray(),
      db.gallery.toArray(),
      db.moments.toArray(),
      db.automationEvents.toArray(),
      db.backupLedgerEntries.toArray(),
    ]);
  return { conversations, messages, presets, providers, memories, gallery, moments, automationEvents, backupLedgerEntries };
}

async function restoreDatabaseSnapshot(snapshot: DbSnapshot): Promise<void> {
  const db = getDb();
  await db.transaction(
    'rw',
    [
      db.conversations,
      db.messages,
      db.presets,
      db.providers,
      db.memories,
      db.gallery,
      db.moments,
      db.automationEvents,
      db.backupLedgerEntries,
    ],
    async () => {
      await db.conversations.clear();
      await db.messages.clear();
      await db.presets.clear();
      await db.providers.clear();
      await db.memories.clear();
      await db.gallery.clear();
      await db.moments.clear();
      await db.automationEvents.clear();
      await db.backupLedgerEntries.clear();
      if (snapshot.conversations.length) await db.conversations.bulkPut(snapshot.conversations);
      if (snapshot.messages.length) await db.messages.bulkPut(snapshot.messages);
      if (snapshot.presets.length) await db.presets.bulkPut(snapshot.presets);
      if (snapshot.providers.length) await db.providers.bulkPut(snapshot.providers);
      if (snapshot.memories.length) await db.memories.bulkPut(snapshot.memories);
      if (snapshot.gallery.length) await db.gallery.bulkPut(snapshot.gallery);
      if (snapshot.moments.length) await db.moments.bulkPut(snapshot.moments);
      if (snapshot.automationEvents.length) await db.automationEvents.bulkPut(snapshot.automationEvents);
      if (snapshot.backupLedgerEntries.length) {
        await db.backupLedgerEntries.bulkPut(snapshot.backupLedgerEntries);
      }
    },
  );
}

function ledgerRows(
  history: BackupAccountHistory,
  backupId: string,
  userId: string,
): BackupLedgerEntry[] {
  if (!history.included || history.capturedAt === null) return [];
  const capturedAt = history.capturedAt;
  return history.entries.map((entry) => ({
    id: `${backupId}:${entry.id}`,
    userId,
    backupId,
    capturedAt,
    entry,
  }));
}

async function writeDatabase(
  archive: BackupArchive,
  mode: 'merge' | 'replace',
  userId: string,
): Promise<void> {
  const db = getDb();
  const collections = archive.collections;
  const messages = restoreMessages(collections.messages, archive.media);
  const presets = restorePresets(collections.presets, archive.media);
  const gallery = restoreGallery(collections.gallery, archive.media);
  const moments = restoreMoments(collections.moments, archive.media);
  const backupRows = ledgerRows(archive.accountHistory, archive.manifest.backupId, userId);

  await db.transaction(
    'rw',
    [
      db.conversations,
      db.messages,
      db.presets,
      db.providers,
      db.memories,
      db.gallery,
      db.moments,
      db.automationEvents,
      db.backupLedgerEntries,
    ],
    async () => {
      if (mode === 'replace') {
        await db.conversations.clear();
        await db.messages.clear();
        await db.presets.clear();
        await db.providers.clear();
        await db.memories.clear();
        await db.gallery.clear();
        await db.moments.clear();
        await db.automationEvents.clear();
        await db.backupLedgerEntries.where('userId').equals(userId).delete();
      }
      if (collections.conversations.length) await db.conversations.bulkPut(collections.conversations);
      if (messages.length) await db.messages.bulkPut(messages);
      if (presets.length) await db.presets.bulkPut(presets);
      if (collections.providers.length) await db.providers.bulkPut(collections.providers);
      if (collections.memories.length) await db.memories.bulkPut(collections.memories);
      if (gallery.length) await db.gallery.bulkPut(gallery);
      if (moments.length) await db.moments.bulkPut(moments);
      if (collections.automationEvents.length) await db.automationEvents.bulkPut(collections.automationEvents);
      if (backupRows.length) await db.backupLedgerEntries.bulkPut(backupRows);
    },
  );
}

function toError(error: unknown): BackupError {
  if (error instanceof BackupError) return error;
  return new BackupError('RESTORE', error instanceof Error ? error.message : '恢复备份失败');
}

/**
 * 解密、校验并恢复。
 *
 * 完整替换失败时：IndexedDB / localStorage 回滚到操作前快照；已下载的安全备份
 * 作为最终兜底。服务端 TTS 偏好失败只记录 warning，不影响本地数据。
 */
export async function restoreEncryptedBackup(
  data: Uint8Array,
  password: string,
  options: RestoreBackupOptions,
): Promise<RestoreResult> {
  if (options.userId.trim() === '') throw new BackupError('LOGIN_REQUIRED', '请先登录再恢复备份');
  if (options.mode !== 'merge' && options.mode !== 'replace') {
    throw new BackupError('VALIDATION', '未知的恢复模式');
  }
  if (!isEncryptedBackup(data)) {
    throw new BackupError('FORMAT', '不是有效的 .zxbak 加密备份文件');
  }
  options.onProgress?.('decrypt');
  let archive: BackupArchive;
  try {
    const zip = await decryptContainer(data, password);
    archive = await unpackArchive(zip);
  } catch (error) {
    throw toError(error);
  }
  if (archive.manifest.account.id !== options.userId) {
    throw new BackupError('ACCOUNT_MISMATCH', '这份备份属于其他账号，当前账号无法恢复');
  }

  const warnings = [...archive.manifest.warnings];
  if (
    options.email !== undefined &&
    archive.manifest.account.email !== '' &&
    options.email !== archive.manifest.account.email
  ) {
    warnings.push('备份邮箱与当前账号邮箱不一致，但账号 ID 相同；流水仍按账号 ID 归档');
  }
  if (!archive.accountHistory.included) {
    warnings.push(archive.accountHistory.warning ?? '备份未包含完整余额流水，本次不写入流水快照');
  }

  const api = options.api;
  const tts: BackupTtsApi = options.tts ?? defaultTtsApi;
  const mimo = options.mimo ?? mimoStorage;
  const now = options.now ?? Date.now();
  let safetyBackup: { filename: string; blob: Blob } | undefined;

  if (options.mode === 'replace') {
    options.onProgress?.('safety-backup');
    const safety = await collectEncryptedBackup(password, {
      account: { id: options.userId, email: options.email ?? archive.manifest.account.email },
      ...(api === undefined ? {} : { api }),
      tts,
      mimo,
      now,
      ...(options.randomBytes === undefined ? {} : { randomBytes: options.randomBytes }),
    });
    safetyBackup = { filename: safety.filename, blob: safety.blob };
    if (options.downloadSafetyBackup !== undefined) {
      options.downloadSafetyBackup(safetyBackup.filename, safetyBackup.blob);
    }
  }

  options.onProgress?.('snapshot');
  const dbSnapshot = await snapshotDatabase();
  const localStorageSnapshot = snapshotAllLocalStorage();
  let mimoSnapshot: MimoSnapshot = { audio: [], modes: [] };
  let mimoSnapshotAvailable = true;
  try {
    mimoSnapshot = await mimo.exportSnapshot(options.userId);
  } catch {
    mimoSnapshotAvailable = false;
    warnings.push('当前 TTS 数据快照读取失败，完整替换失败时可能无法恢复本地语音缓存');
  }

  let restoredMimo: MimoSnapshot;
  try {
    restoredMimo = await restoreMimoSnapshot(archive.mimo, options.userId, archive.media);
  } catch (error) {
    throw toError(error);
  }

  try {
    options.onProgress?.('restore-db');
    await writeDatabase(archive, options.mode, options.userId);
    options.onProgress?.('restore-settings');
    applyBackupLocalStorage(archive.localStorage, options.mode, options.userId);
    options.onProgress?.('restore-tts');
    await mimo.restoreSnapshot(options.userId, restoredMimo, options.mode);
  } catch (error) {
    options.onProgress?.('rollback');
    try {
      await restoreDatabaseSnapshot(dbSnapshot);
    } catch {
      warnings.push('IndexedDB 回滚失败，请使用已下载的安全备份恢复');
    }
    try {
      restoreAllLocalStorage(localStorageSnapshot);
    } catch {
      warnings.push('localStorage 回滚失败，请使用已下载的安全备份恢复');
    }
    if (mimoSnapshotAvailable) {
      try {
        await mimo.restoreSnapshot(options.userId, mimoSnapshot, 'replace');
      } catch {
        warnings.push('TTS 本地缓存回滚失败');
      }
    } else {
      warnings.push('TTS 本地缓存未建立回滚快照，无法自动回滚');
    }
    throw toError(error);
  }

  let ttsPreferences: RestoreResult['ttsPreferences'] = 'skipped';
  if (archive.mimo.serverPreferences !== undefined && tts !== undefined) {
    options.onProgress?.('restore-server-tts');
    try {
      await tts.savePreferences(archive.mimo.serverPreferences);
      ttsPreferences = 'restored';
    } catch {
      ttsPreferences = 'failed';
      warnings.push('本地数据已恢复，但服务端 TTS 音色偏好保存失败，请在语音与 TTS 页面重新保存');
    }
  }

  options.onProgress?.('done');
  const counts: RestoreCounts = {
    conversations: archive.collections.conversations.length,
    messages: archive.collections.messages.length,
    presets: archive.collections.presets.length,
    providers: archive.collections.providers.length,
    memories: archive.collections.memories.length,
    gallery: archive.collections.gallery.length,
    moments: archive.collections.moments.length,
    automationEvents: archive.collections.automationEvents.length,
    media: archive.media.size,
    ledger: archive.accountHistory.included ? archive.accountHistory.entries.length : 0,
    ttsAudio: restoredMimo.audio.length,
  };
  return {
    mode: options.mode,
    counts,
    warnings,
    ttsPreferences,
    ...(safetyBackup === undefined ? {} : { safetyBackup }),
  };
}

/** 兼容旧调用：创建并返回加密备份。 */
export async function createEncryptedBackup(
  password: string,
  options: CreateBackupOptions = {},
): Promise<EncryptedBackup> {
  return collectEncryptedBackup(password, options);
}
